import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { TrophyIcon, Share2Icon, ListIcon, UsersIcon } from 'lucide-react';
import confetti from 'canvas-confetti';
interface WinnerScreenProps {
  gameMode: string;
  players: string[];
  scores: Record<string, number[]>;
  onShare: () => void;
  onLeaderboard: () => void;
}
export function WinnerScreen({
  gameMode,
  players,
  scores,
  onShare,
  onLeaderboard
}: WinnerScreenProps) {
  // Calculate winner based on game mode
  const getWinnerData = () => {
    if (gameMode === 'challenge') {
      // Highest points wins
      const totals = players.map(p => ({
        name: p,
        score: scores[p].reduce((a, b) => a + b, 0)
      })).sort((a, b) => b.score - a.score); // Descending
      return {
        title: 'Challenge Champion',
        winner: totals[0],
        others: totals.slice(1),
        scoreLabel: 'Total Points',
        icon: <TrophyIcon className="w-10 h-10 text-brand-sand" data-id="element-327" />,
        color: 'text-brand-sand'
      };
    } else if (gameMode === 'match' && players.length === 2) {
      // Match play logic
      let p1Wins = 0;
      let p2Wins = 0;
      const p1 = players[0];
      const p2 = players[1];
      for (let i = 0; i < scores[p1].length; i++) {
        const s1 = scores[p1][i];
        const s2 = scores[p2][i];
        if (s1 > 0 && s2 > 0) {
          if (s1 < s2) p1Wins++;else if (s2 < s1) p2Wins++;
        }
      }
      let statusText = 'Tied Match';
      let winnerName = 'Draw';
      if (p1Wins > p2Wins) {
        statusText = `${p1Wins - p2Wins} UP`;
        winnerName = p1;
      } else if (p2Wins > p1Wins) {
        statusText = `${p2Wins - p1Wins} UP`;
        winnerName = p2;
      }
      return {
        title: 'Match Winner',
        winner: {
          name: winnerName,
          score: statusText
        },
        others: [{
          name: winnerName === p1 ? p2 : p1,
          score: ''
        }],
        scoreLabel: 'Final Result',
        icon: <TrophyIcon className="w-10 h-10 text-brand-gold" data-id="element-328" />,
        color: 'text-brand-gold'
      };
    } else if (gameMode === 'bestball' && players.length === 4) {
      // Best ball logic (simplified for demo)
      const t1Score = scores[players[0]].reduce((a, b) => a + b, 0) + scores[players[1]].reduce((a, b) => a + b, 0);
      const t2Score = scores[players[2]].reduce((a, b) => a + b, 0) + scores[players[3]].reduce((a, b) => a + b, 0);
      const t1Won = t1Score < t2Score;
      return {
        title: 'Winning Team',
        winner: {
          name: t1Won ? 'Team 1' : 'Team 2',
          score: t1Won ? t1Score : t2Score
        },
        others: [{
          name: t1Won ? 'Team 2' : 'Team 1',
          score: t1Won ? t2Score : t1Score
        }],
        scoreLabel: 'Team Total',
        icon: <UsersIcon className="w-10 h-10 text-brand-green-light" data-id="element-329" />,
        color: 'text-brand-green-light'
      };
    } else {
      // Default: Stroke play (lowest wins)
      const totals = players.map(p => ({
        name: p,
        score: scores[p].reduce((a, b) => a + b, 0)
      })).sort((a, b) => a.score - b.score); // Ascending
      return {
        title: 'Champion',
        winner: totals[0],
        others: totals.slice(1),
        scoreLabel: 'Final Score',
        icon: <TrophyIcon className="w-10 h-10 text-brand-gold" data-id="element-330" />,
        color: 'text-brand-gold'
      };
    }
  };
  const data = getWinnerData();
  useEffect(() => {
    // Fire confetti on mount
    const duration = 3 * 1000;
    const animationEnd = Date.now() + duration;
    const defaults = {
      startVelocity: 30,
      spread: 360,
      ticks: 60,
      zIndex: 0
    };
    const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min;
    const interval: any = setInterval(function () {
      const timeLeft = animationEnd - Date.now();
      if (timeLeft <= 0) {
        return clearInterval(interval);
      }
      const particleCount = 50 * (timeLeft / duration);
      confetti(Object.assign({}, defaults, {
        particleCount,
        origin: {
          x: randomInRange(0.1, 0.3),
          y: Math.random() - 0.2
        },
        colors: ['#B8965A', '#F5F0E8', '#1B3A2D']
      }));
      confetti(Object.assign({}, defaults, {
        particleCount,
        origin: {
          x: randomInRange(0.7, 0.9),
          y: Math.random() - 0.2
        },
        colors: ['#B8965A', '#F5F0E8', '#1B3A2D']
      }));
    }, 250);
    return () => clearInterval(interval);
  }, []);
  return <div className="flex flex-col h-full bg-brand-green-deep relative overflow-hidden" data-id="element-331">
      {/* Background elements */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(184,150,90,0.15)_0%,transparent_70%)]" data-id="element-332" />

      <div className="relative z-10 flex-1 flex flex-col p-6" data-id="element-333">
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.5,
        type: 'spring'
      }} className="flex-1 flex flex-col items-center justify-center text-center mt-8" data-id="element-334">
          <div className={`w-20 h-20 bg-brand-charcoal/40 rounded-full flex items-center justify-center mb-6 border border-brand-cream/20 shadow-[0_0_30px_rgba(184,150,90,0.2)]`} data-id="element-335">
            {data.icon}
          </div>

          <span className={`${data.color} text-sm font-bold uppercase tracking-[0.2em] mb-2`} data-id="element-336">
            {data.title}
          </span>
          <h1 className="text-5xl font-heading text-brand-cream-light mb-2" data-id="element-337">
            {data.winner.name}
          </h1>

          <div className="mt-8 bg-brand-charcoal/40 backdrop-blur-md border border-brand-cream/10 rounded-2xl p-6 w-full max-w-xs" data-id="element-338">
            <span className="text-brand-cream/60 text-xs uppercase tracking-widest block mb-1" data-id="element-339">
              {data.scoreLabel}
            </span>
            <span className={`text-5xl font-heading ${data.color}`} data-id="element-340">
              {data.winner.score}
            </span>
            <span className="text-brand-cream/40 text-sm block mt-2" data-id="element-341">
              The Sunset Green
            </span>
          </div>
        </motion.div>

        {/* Other players */}
        {data.others.length > 0 && <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.5
      }} className="mb-8 space-y-2" data-id="element-342">
            {data.others.map((p, i) => <div key={p.name} className="flex justify-between items-center bg-brand-cream/5 rounded-lg px-4 py-3 text-sm font-body" data-id="element-343">
                <span className="text-brand-cream/80" data-id="element-344">
                  <span className="text-brand-cream/40 mr-2" data-id="element-345">
                    {gameMode === 'match' ? '' : i + 2 + '.'}
                  </span>
                  {p.name}
                </span>
                <span className="text-brand-cream-light font-bold" data-id="element-346">
                  {p.score}
                </span>
              </div>)}
          </motion.div>}

        {/* Actions */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.7
      }} className="space-y-3 mt-auto" data-id="element-347">
          <button onClick={onShare} className="w-full flex items-center justify-center space-x-2 bg-brand-gold text-brand-charcoal font-heading text-xl py-4 rounded-xl shadow-lg hover:bg-brand-gold-light transition-colors active:scale-[0.98]" data-id="element-348">
            <Share2Icon className="w-5 h-5" data-id="element-349" />
            <span data-id="element-350">Share Result</span>
          </button>

          <button onClick={onLeaderboard} className="w-full flex items-center justify-center space-x-2 bg-brand-charcoal/50 text-brand-cream-light font-body py-4 rounded-xl border border-brand-cream/10 hover:bg-brand-charcoal transition-colors active:scale-[0.98]" data-id="element-351">
            <ListIcon className="w-5 h-5 text-brand-gold" data-id="element-352" />
            <span data-id="element-353">Full Leaderboard</span>
          </button>
        </motion.div>
      </div>
    </div>;
}